import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import {
  createUserWithEmailAndPassword,
} from "firebase/auth";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Button, Card, Form, Table, Alert, Spinner } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function CreateUserPage() {
  const [users, setUsers] = useState([]);
  // Keep a copy of all users so we can toggle between filters
  const [originalUsers, setOriginalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, "Users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const role = userDoc.data().role;
          setCurrentUserRole(role);

          if (role !== "admin") {
            setAccessDenied(true); // Restrict access for non-admin users
          }
        }
      }
    };

    const fetchUsersAndRoles = async () => {
      setLoading(true);
      try {
        // Fetch existing users
        const usersSnapshot = await getDocs(collection(db, "Users"));
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
        // Also store the full, original list for later filtering
        setOriginalUsers(usersList);

        // Fetch roles
        const rolesSnapshot = await getDocs(collection(db, "Roles"));
        const rolesList = rolesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRoles(rolesList);
      } catch (error) {
        console.error("Error fetching users or roles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUserRole();
    fetchUsersAndRoles();
  }, []);

  const handleCreateUser = async () => {
    if (!email || !password || !fullName || !role) {
      setFeedback("All fields are required.");
      return;
    }

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // Add user details to Firestore
      await setDoc(doc(db, "Users", uid), {
        email,
        fullName,
        role,
        uid,
        createdAt: serverTimestamp(),
      });

      setFeedback("User created successfully!");
      setEmail("");
      setPassword("");
      setFullName("");
      setRole("user");

      // Refresh user list
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const freshUsers = usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUsers(freshUsers);
      setOriginalUsers(freshUsers);
    } catch (error) {
      console.error("Error creating user:", error);
      setFeedback("Failed to create user. Please try again.");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      // Update the user's role to 'deleted'
      const userDocRef = doc(db, "Users", userId);
      await updateDoc(userDocRef, { role: "deleted" });

      setFeedback(`User's role updated to 'deleted'.`);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: "deleted" } : user
        )
      );
      // Also update originalUsers for consistency
      setOriginalUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: "deleted" } : user
        )
      );
    } catch (error) {
      console.error("Error updating user role:", error);
      setFeedback("Failed to update user role. Please try again.");
    }
  };

  // Filter out users who do NOT have the "deleted" role
  const handleFilterDeletedUsers = () => {
    setUsers(originalUsers.filter((user) => user.role !== "deleted"));
  };

  // Show only users who have the "deleted" role
  const handleShowDeletedUsers = () => {
    setUsers(originalUsers.filter((user) => user.role === "deleted"));
  };

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">Only admins can access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <h4 className="main-title mb-4">Create User</h4>

        {feedback && <Alert variant="info">{feedback}</Alert>}

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <h5 className="mb-3">New User Details</h5>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Email (will be used to login)</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Button variant="success" onClick={handleCreateUser}>
                Create User
              </Button>
            </Form>
          </Card.Body>
        </Card>

        <h5 className="mb-3">Existing Users</h5>
        {/* Buttons for filtering out deleted users or showing only deleted users */}
        <div className="mb-3">
          <Button variant="primary" onClick={handleFilterDeletedUsers}>
           Don't Show Deleted
          </Button>{" "}
          <Button variant="secondary" onClick={handleShowDeletedUsers}>
            Show Deleted
          </Button>
        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : (
          <Card className="shadow-sm">
            <Card.Body>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Email </th>
                    <th>User Name</th>
                    <th>Role</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id}>
                      <td>{index + 1}</td>
                      <td>{user.email}</td>
                      <td>{user.fullName}</td>
                      <td>{user.role}</td>
                      <td>{new Date(user.createdAt?.seconds * 1000).toLocaleString()}</td>
                      <td>
                        <Button
                          variant="danger"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Mark as Deleted
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </div>
      <Footer />
    </React.Fragment>
  );
}
